import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { liveLinkStatusService, ImportUrlItem } from '../../services/liveLinkStatusService';

interface ImportLinksModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ImportLinksModal: React.FC<ImportLinksModalProps> = ({ open, onClose, onSuccess }) => {
  const [csvText, setCsvText] = useState('');
  const [importResults, setImportResults] = useState<{
    success: string[];
    failed: Array<{ url: string; error: string }>;
  } | null>(null);

  const importMutation = useMutation({
    mutationFn: (urls: ImportUrlItem[]) => liveLinkStatusService.importUrls(urls),
    onSuccess: (response) => {
      console.log('[ImportModal] Import success response:', response);
      setImportResults(response.data);
      // Don't call onSuccess() here as it closes the modal
      // Modal should stay open for multiple imports
    },
    onError: (error) => {
      console.error('[ImportModal] Import error:', error);
      // Show error message without closing modal
      alert(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    },
  });

  const handleClose = () => {
    setCsvText('');
    setImportResults(null);
    onSuccess(); // Refresh main page data when user manually closes
    onClose();
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        setCsvText(text);
        // Clear previous results when new file is uploaded
        setImportResults(null);
      };
      reader.readAsText(file);
    }
  };

  const handleReset = () => {
    setCsvText('');
    setImportResults(null);
    // Reset file input
    const fileInput = document.getElementById('csv-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleImport = () => {
    const lines = csvText.split('\n').filter(line => line.trim());
    const urls: ImportUrlItem[] = [];

    // Skip header if present
    const startIndex = lines[0]?.toLowerCase().includes('url') ? 1 : 0;

    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const parts = line.split(',').map(p => p.trim());
      const url = parts[0];
      const publisherEmail = parts[1] || undefined;

      if (url) {
        urls.push({ url, publisherEmail });
      }
    }

    if (urls.length === 0) {
      alert('No valid URLs found in the CSV');
      return;
    }

    importMutation.mutate(urls);
  };

  const downloadTemplate = () => {
    const template = 'URL,Publisher Email\nhttps://example.com/article,publisher@example.com\nhttps://example2.com/post,';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'link_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
        }
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        }
      }}
    >
      <DialogTitle sx={{ 
        fontSize: '18px', 
        fontWeight: 700, 
        color: '#111827',
        borderBottom: '1px solid #E5E7EB',
        pb: 2
      }}>
        Import External URLs
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '13px', lineHeight: 1.6 }} gutterBottom>
            Upload a CSV file with URLs to monitor. Format: URL, Publisher Email
          </Typography>
          <Button
            size="small"
            onClick={downloadTemplate}
            sx={{ 
              mt: 1.5,
              fontSize: '12.5px',
              fontWeight: 500,
              color: '#4F46E5',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#EEF2FF'
              }
            }}
          >
            Download Template
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <input
            accept=".csv"
            style={{ display: 'none' }}
            id="csv-upload"
            type="file"
            onChange={handleFileUpload}
          />
          <label htmlFor="csv-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
              fullWidth
              sx={{
                borderColor: '#C7D2FE',
                color: '#4F46E5',
                fontWeight: 500,
                fontSize: '13px',
                textTransform: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                '&:hover': {
                  borderColor: '#A5B4FC',
                  backgroundColor: '#EEF2FF'
                }
              }}
            >
              Upload CSV File
            </Button>
          </label>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={10}
          label="CSV Content"
          placeholder="URL,Publisher Email&#10;https://example.com/article,publisher@example.com"
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          sx={{ 
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              fontSize: '13px',
              fontFamily: 'monospace',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#D1D5DB'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4F46E5',
                borderWidth: '2px'
              }
            },
            '& .MuiInputLabel-root': {
              fontSize: '13px',
              fontWeight: 500
            }
          }}
        />

        {importMutation.isPending && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        )}

        {importResults && (
          <Box sx={{ mt: 2 }}>
            {/* Comprehensive Summary Message */}
            <Alert 
              severity={importResults.failed.length > 0 ? "warning" : "success"} 
              sx={{ mb: 2 }}
            >
              <strong>Import Complete!</strong><br />
              ✅ Successfully imported: {importResults.success.length} URLs<br />
              {importResults.failed.length > 0 && (
                <>❌ Failed/Skipped: {importResults.failed.length} URLs</>
              )}
              {importResults.failed.length === 0 && "🎉 All URLs imported successfully!"}
            </Alert>

            {/* Detailed Success List */}
            {importResults.success.length > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Successfully Imported URLs ({importResults.success.length}):
                </Typography>
                <List dense>
                  {importResults.success.slice(0, 5).map((url, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary={`✓ ${url}`} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                  {importResults.success.length > 5 && (
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary={`... and ${importResults.success.length - 5} more URLs`}
                        primaryTypographyProps={{ variant: 'body2', style: { fontStyle: 'italic' } }}
                      />
                    </ListItem>
                  )}
                </List>
              </Alert>
            )}

            {/* Detailed Failed List */}
            {importResults.failed.length > 0 && (
              <>
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Failed/Skipped URLs ({importResults.failed.length}):
                  </Typography>
                  <List dense>
                    {importResults.failed.map((item, index) => (
                      <React.Fragment key={index}>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={`❌ ${item.url}`}
                            secondary={item.error}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                        {index < importResults.failed.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Alert>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ 
        borderTop: '1px solid #E5E7EB', 
        px: 3, 
        py: 2,
        gap: 1
      }}>
        <Button 
          onClick={handleClose}
          sx={{
            color: '#374151',
            fontWeight: 500,
            fontSize: '13px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#F9FAFB'
            }
          }}
        >
          Close
        </Button>
        {(csvText || importResults) && (
          <Button 
            onClick={handleReset}
            sx={{
              color: '#EA580C',
              fontWeight: 500,
              fontSize: '13px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#FFF7ED'
              }
            }}
          >
            Reset
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!csvText.trim() || importMutation.isPending}
          sx={{
            backgroundColor: '#4F46E5',
            fontWeight: 500,
            fontSize: '13px',
            textTransform: 'none',
            boxShadow: '0 1px 2px rgba(79,70,229,0.25)',
            '&:hover': {
              backgroundColor: '#4338CA',
              boxShadow: '0 3px 8px rgba(79,70,229,0.3)'
            },
            '&:disabled': {
              backgroundColor: '#E5E7EB',
              color: '#9CA3AF'
            }
          }}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportLinksModal;
